import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import {
  useProjectMilestones,
  useDeleteMilestone,
} from '../../../api/hooks/useProjects';
import { useNotifications } from '../../../context/NotificationContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { MilestoneFormDialog } from './MilestoneFormDialog';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';

interface Milestone {
  id: string;
  name: string;
  description: string | null;
  state: string;
  deadline: string | null;
  rank: string;
  responsible_coworker_id: string | null;
  responsible_name?: string;
  [key: string]: unknown;
}

export function MilestoneSection({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data, isLoading } = useProjectMilestones(projectId);
  const deleteMutation = useDeleteMilestone();

  const milestones = (data as unknown as { data?: Milestone[] } | undefined)?.data ?? [];
  // Rang ist nicht mehr bearbeitbar (PHQ-83): Sortierung nach Deadline, ohne Deadline zuletzt.
  const sorted = [...milestones].sort((a, b) => (a.deadline ?? '9999').localeCompare(b.deadline ?? '9999'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Milestone | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Milestone | null>(null);

  const handleCreate = () => {
    setEditItem(null);
    setDialogOpen(true);
  };

  const handleEdit = (item: Milestone) => {
    setEditItem(item);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ projectId, milestoneId: deleteTarget.id });
      notify(tc('status.saved', 'Gelöscht'), 'success');
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    }
    setDeleteTarget(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('milestones.title', 'Meilensteine')}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={handleCreate}>
          {t('milestones.add', 'Meilenstein hinzufügen')}
        </Button>
      </Box>

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('milestones.empty', 'Keine Meilensteine vorhanden')}
        </Typography>
      ) : (
        <List disablePadding>
          {sorted.map((ms) => (
            <ListItem
              key={ms.id}
              divider
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={tc('actions.edit', 'Bearbeiten')}>
                    <IconButton size="small" onClick={() => handleEdit(ms)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tc('actions.delete', 'Löschen')}>
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(ms)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1" fontWeight={500}>{ms.name}</Typography>
                  </Stack>
                }
                secondary={
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                    {ms.deadline && (
                      <Typography variant="caption" color="text.secondary">
                        {t('milestones.deadline', 'Deadline')}: {new Date(ms.deadline).toLocaleDateString('de-DE')}
                      </Typography>
                    )}
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      <MilestoneFormDialog
        open={dialogOpen}
        mode={editItem ? 'edit' : 'create'}
        projectId={projectId}
        initialData={editItem ?? undefined}
        existingNames={milestones.map((m) => m.name)}
        onClose={() => setDialogOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={tc('actions.delete', 'Löschen')}
        message={`${t('milestones.confirmDelete', 'Meilenstein wirklich löschen?')} "${deleteTarget?.name ?? ''}"`}
        confirmColor="error"
        confirmLabel={tc('actions.delete', 'Löschen')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </Paper>
  );
}
